"use client";

import { useEffect, useState } from "react";
import { 
    PromptSummary, PromptDetail, PromptUpdate, 
    getPromptConsoleAccess, getPrompts, getPromptDetail, updatePrompt, resetPrompt, revertPrompt 
} from "@/lib/prompts/api";

function Badge({ children, variant = "default" }: { children: React.ReactNode, variant?: "default" | "warning" | "info" | "error" }) {
    const colors = {
        default: "bg-[#e8edeb] text-[#3a4d4f]",
        warning: "bg-[#fbecd6] text-[#9a6a16]",
        info: "bg-[#e2f0f9] text-[#006699]",
        error: "bg-[#fde2dd] text-[#ae232d]"
    };
    return (
        <span className={`text-[10px] font-bold tracking-wide rounded px-1.5 py-0.5 ${colors[variant]}`}>
            {children}
        </span>
    );
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

export function PromptConsolePanel() {
    const [access, setAccess] = useState<boolean | null>(null);
    const [canEdit, setCanEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [promptsMap, setPromptsMap] = useState<Record<string, PromptSummary[]>>({});
    
    // Editor State
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [detail, setDetail] = useState<PromptDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    
    // Form State
    const [systemTemplate, setSystemTemplate] = useState("");
    const [userTemplate, setUserTemplate] = useState("");
    const [temperature, setTemperature] = useState(0.5);
    const [maxTokens, setMaxTokens] = useState<number | null>(null);
    const [changeNote, setChangeNote] = useState("");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // UI State
    const [showDiff, setShowDiff] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [search, setSearch] = useState("");

    const loadList = async () => {
        try {
            const data = await getPrompts();
            setPromptsMap(data);
        } catch (e: unknown) {
            setError(errorMessage(e));
        }
    };

    useEffect(() => {
        getPromptConsoleAccess()
            .then(res => {
                setAccess(res.can_view || res.is_prompt_admin);
                setCanEdit(res.can_edit);
                if (res.can_view || res.is_prompt_admin) {
                    loadList().finally(() => setLoading(false));
                } else {
                    setLoading(false);
                }
            })
            .catch((e: unknown) => {
                setAccess(false);
                setError(errorMessage(e));
                setLoading(false);
            });
    }, []);

    const selectPrompt = async (key: string) => {
        setSelectedKey(key);
        setDetailLoading(true);
        setSaveError(null);
        setShowDiff(false);
        setShowHistory(false);
        setChangeNote("");
        try {
            const data = await getPromptDetail(key);
            setDetail(data);
            setSystemTemplate(data.system_template);
            setUserTemplate(data.user_template || "");
            setTemperature(data.temperature);
            setMaxTokens(data.max_tokens);
        } catch (e: unknown) {
            setSaveError(errorMessage(e));
        } finally {
            setDetailLoading(false);
        }
    };

    const handleSave = async () => {
        if (!detail) return;
        if (!changeNote.trim()) {
            setSaveError("Change note is required.");
            return;
        }
        setIsSaving(true);
        setSaveError(null);
        try {
            const payload: PromptUpdate = {
                system_template: systemTemplate,
                user_template: userTemplate.trim() || null,
                temperature,
                max_tokens: maxTokens,
                version: detail.version,
                change_note: changeNote.trim()
            };
            const updated = await updatePrompt(detail.key, payload);
            setDetail(updated);
            setSystemTemplate(updated.system_template);
            setUserTemplate(updated.user_template || "");
            setTemperature(updated.temperature);
            setMaxTokens(updated.max_tokens);
            setChangeNote("");
            loadList(); // Refresh list to update badges
        } catch (e: unknown) {
            setSaveError(errorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        if (!detail) return;
        if (!confirm("This will discard all customizations and restore the original code default prompt. Continue?")) return;
        setIsSaving(true);
        try {
            const updated = await resetPrompt(detail.key, detail.version);
            setDetail(updated);
            setSystemTemplate(updated.system_template);
            setUserTemplate(updated.user_template || "");
            setTemperature(updated.temperature);
            setMaxTokens(updated.max_tokens);
            setChangeNote("");
            loadList();
        } catch (e: unknown) {
            setSaveError(errorMessage(e));
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleRevert = async (version: number) => {
        if (!detail) return;
        const note = prompt(`Revert to version ${version}? Enter a change note (optional):`);
        if (note === null) return; // user cancelled
        setIsSaving(true);
        try {
            const updated = await revertPrompt(detail.key, { version, change_note: note || `Reverted to v${version}` });
            setDetail(updated);
            setSystemTemplate(updated.system_template);
            setUserTemplate(updated.user_template || "");
            setTemperature(updated.temperature);
            setMaxTokens(updated.max_tokens);
            setChangeNote("");
            loadList();
        } catch (e: unknown) {
            setSaveError(errorMessage(e));
        } finally {
            setIsSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="py-16 flex flex-col items-center justify-center gap-3">
                <div className="w-8 h-8 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
            </div>
        );
    }

    if (!access) {
        return (
            <div className="max-w-xl mx-auto text-center py-16 px-6">
                <div className="text-4xl">🔒</div>
                <h1 className="mt-3.5 mb-1.5 text-[22px] font-bold text-[#15302f]">Prompts Console is restricted</h1>
                <p className="text-[#5f7173] text-sm leading-relaxed">
                    This module is available to staff. Only admins and superadmins can make changes.
                </p>
            </div>
        );
    }

    // Filter list
    const workflows = Object.keys(promptsMap).sort();
    
    // Check missing placeholders live
    const missingPlaceholders = detail?.required_placeholders.filter(p => {
        const fullText = systemTemplate + (userTemplate || "");
        return !fullText.includes(`{${p}}`);
    }) || [];

    return (
        <div className="flex h-[calc(100vh-120px)] -mx-6 -my-4 border-t border-[#eef1ef]">
            {/* Left Panel: List */}
            <div className="w-[320px] bg-[#fcfdfd] border-r border-[#eef1ef] flex flex-col overflow-hidden">
                <div className="p-4 border-b border-[#eef1ef] bg-white">
                    <h2 className="font-bold text-[#15302f] mb-3">Prompt Registry</h2>
                    <input 
                        type="text"
                        placeholder="Search prompts..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full border border-[#d9dedb] rounded-lg px-3 py-1.5 text-[13px] bg-[#fafbfa] focus:outline-none focus:border-[#4b8892]"
                    />
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                    {error && (
                        <div className="m-2 rounded-md border border-[#f5b8b1] bg-[#fde2dd] p-3 text-[12px] text-[#ae232d]">
                            Could not load prompts: {error}
                        </div>
                    )}
                    {workflows.map(wf => {
                        const items = promptsMap[wf].filter(p => p.label.toLowerCase().includes(search.toLowerCase()) || p.key.toLowerCase().includes(search.toLowerCase()));
                        if (items.length === 0) return null;
                        
                        return (
                            <div key={wf} className="mb-4">
                                <div className="px-3 py-1 text-[11px] font-bold text-[#88a39b] uppercase tracking-wider">{wf}</div>
                                {items.map(p => (
                                    <button 
                                        key={p.key}
                                        onClick={() => selectPrompt(p.key)}
                                        className={`w-full text-left px-3 py-2 rounded-md mb-0.5 ${selectedKey === p.key ? 'bg-[#e2f0f9] border border-[#bae0f5]' : 'hover:bg-[#f4f6f5] border border-transparent'}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-bold text-[13px] text-[#004d57]">{p.key}</span>
                                            <div className="flex gap-1">
                                                {p.code_default_changed && <Badge variant="info">!</Badge>}
                                                {p.is_customized && <Badge variant="warning">MOD</Badge>}
                                            </div>
                                        </div>
                                        <div className="text-[11.5px] text-[#5f7173] truncate mt-0.5">{p.label}</div>
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {/* Right Panel: Editor */}
            <div className="flex-1 bg-white flex flex-col overflow-hidden">
                {!selectedKey ? (
                    <div className="flex-1 flex items-center justify-center text-[#88a39b] text-[13px]">
                        Select a prompt to edit
                    </div>
                ) : detailLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                    </div>
                ) : detail ? (
                    <>
                        {/* Editor Header */}
                        <div className="px-6 py-4 border-b border-[#eef1ef] flex items-start justify-between bg-[#fcfdfd]">
                            <div>
                                <h1 className="text-xl font-bold text-[#15302f]">{detail.label} <span className="text-[14px] font-normal text-[#5f7173] ml-2 font-mono">{detail.key}</span></h1>
                                <p className="text-[13px] text-[#5f7173] mt-1.5 max-w-2xl">{detail.description}</p>
                                
                                <div className="flex items-center gap-2 mt-3">
                                    <Badge>v{detail.version}</Badge>
                                    {detail.is_customized && <Badge variant="warning">Customized</Badge>}
                                    {detail.code_default_changed && <Badge variant="info">Code Default Changed</Badge>}
                                    {detail.response_format && <Badge variant="default">Format: {detail.response_format}</Badge>}
                                </div>
                            </div>
                            
                            <div className="flex flex-col gap-2 items-end">
                                <div className="flex gap-2">
                                    <button onClick={() => setShowHistory(!showHistory)} className="text-[12px] font-bold text-[#3a6b78] hover:underline">
                                        {showHistory ? "Hide History" : "History"}
                                    </button>
                                    <span className="text-[#d9dedb]">|</span>
                                    <button onClick={() => setShowDiff(!showDiff)} className="text-[12px] font-bold text-[#3a6b78] hover:underline">
                                        {showDiff ? "Hide Diff" : "Compare Default"}
                                    </button>
                                </div>
                                {detail.is_customized && canEdit && (
                                    <button onClick={handleReset} disabled={isSaving} className="text-[11px] text-[#ae232d] hover:underline font-bold mt-1">
                                        Reset to Code Default
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {/* Editor Body */}
                        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                            {saveError && (
                                <div className="bg-[#fde2dd] border border-[#f5b8b1] rounded-lg p-3 text-[13px] text-[#ae232d]">
                                    <strong className="font-bold">Error saving:</strong> {saveError}
                                </div>
                            )}
                            
                            {/* History Drawer */}
                            {showHistory && (
                                <div className="bg-[#f4f6f5] border border-[#d9dedb] rounded-xl p-4">
                                    <h3 className="text-[13px] font-bold text-[#15302f] mb-3">Version History</h3>
                                    <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                                        {detail.history.map(h => (
                                            <div key={h.version} className="flex items-center justify-between bg-white border border-[#eef1ef] rounded p-2 text-[12px]">
                                                <div>
                                                    <span className="font-bold text-[#004d57]">v{h.version}</span>
                                                    <span className="text-[#88a39b] mx-2">•</span>
                                                    <span className="text-[#3a4d4f]">{new Date(h.created_at).toLocaleString()}</span>
                                                    <span className="text-[#88a39b] mx-2">•</span>
                                                    <span className="text-[#5f7173]">{h.created_by || 'System'}</span>
                                                    <div className="text-[#15302f] mt-0.5">{h.change_note || 'Initial version'}</div>
                                                </div>
                                                {canEdit && (
                                                    <button onClick={() => handleRevert(h.version)} disabled={isSaving} className="text-[#3a6b78] font-bold border border-[#d9dedb] rounded px-2 py-1 hover:bg-[#f4f6f5]">
                                                        Revert
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {detail.history.length === 0 && (
                                            <div className="text-[#88a39b] text-[12px] italic">No saved revisions yet.</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Placeholders check */}
                            {detail.required_placeholders.length > 0 && (
                                <div className="bg-[#fcfdfd] border border-[#eef1ef] rounded-lg p-3">
                                    <div className="text-[12px] font-bold text-[#15302f] mb-2">Required Variables</div>
                                    <div className="flex gap-2 flex-wrap">
                                        {detail.required_placeholders.map(p => {
                                            const isMissing = missingPlaceholders.includes(p);
                                            return (
                                                <div key={p} className={`text-[11.5px] font-mono px-2 py-1 rounded-md border ${isMissing ? 'bg-[#fde2dd] text-[#ae232d] border-[#f5b8b1]' : 'bg-[#eef1f0] text-[#3a4d4f] border-transparent'}`}>
                                                    {'{'}{p}{'}'} {isMissing && " (Missing)"}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* System Template */}
                            <div>
                                <div className="flex justify-between items-end mb-1.5">
                                    <label className="text-[13px] font-bold text-[#15302f]">System Template</label>
                                    {showDiff && <span className="text-[11px] font-bold text-[#006699]">Showing diff vs Code Default</span>}
                                </div>
                                {showDiff ? (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-[11px] text-[#88a39b] mb-1">Code Default</div>
                                            <textarea readOnly value={detail.default_system_template} className="w-full h-[250px] font-mono text-[13px] p-3 border border-[#d9dedb] rounded-lg bg-[#f4f6f5] text-[#5f7173]" />
                                        </div>
                                        <div>
                                            <div className="text-[11px] text-[#004d57] mb-1 font-bold">Current System Template</div>
                                            <textarea readOnly value={systemTemplate} className="w-full h-[250px] font-mono text-[13px] p-3 border border-[#4b8892] rounded-lg bg-[#fcfdfd] text-[#15302f]" />
                                        </div>
                                    </div>
                                ) : (
                                    <textarea 
                                        value={systemTemplate}
                                        onChange={e => setSystemTemplate(e.target.value)}
                                        readOnly={!canEdit}
                                        className={`w-full min-h-[250px] font-mono text-[13px] leading-relaxed p-4 border border-[#d9dedb] rounded-xl focus:outline-none focus:border-[#4b8892] focus:ring-1 focus:ring-[#4b8892] ${canEdit ? "bg-[#fafbfa]" : "bg-[#f4f6f5] text-[#5f7173]"}`}
                                    />
                                )}
                            </div>

                            {/* User Template */}
                            {(detail.user_template !== null || userTemplate !== "") && (
                                <div>
                                    <label className="text-[13px] font-bold text-[#15302f] mb-1.5 block">User Template</label>
                                    {showDiff ? (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <textarea readOnly value={detail.default_user_template || ""} className="w-full h-[150px] font-mono text-[13px] p-3 border border-[#d9dedb] rounded-lg bg-[#f4f6f5] text-[#5f7173]" />
                                            </div>
                                            <div>
                                                <textarea readOnly value={userTemplate} className="w-full h-[150px] font-mono text-[13px] p-3 border border-[#4b8892] rounded-lg bg-[#fcfdfd] text-[#15302f]" />
                                            </div>
                                        </div>
                                    ) : (
                                        <textarea 
                                            value={userTemplate}
                                            onChange={e => setUserTemplate(e.target.value)}
                                            readOnly={!canEdit}
                                            className={`w-full min-h-[150px] font-mono text-[13px] leading-relaxed p-4 border border-[#d9dedb] rounded-xl focus:outline-none focus:border-[#4b8892] focus:ring-1 focus:ring-[#4b8892] ${canEdit ? "bg-[#fafbfa]" : "bg-[#f4f6f5] text-[#5f7173]"}`}
                                        />
                                    )}
                                </div>
                            )}
                            
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="text-[13px] font-bold text-[#15302f] mb-1.5 block">Temperature: {temperature}</label>
                                    <input 
                                        type="range" min="0" max="2" step="0.1" 
                                        value={temperature} onChange={e => setTemperature(parseFloat(e.target.value))}
                                        disabled={!canEdit}
                                        className="w-full"
                                    />
                                    <div className="flex justify-between text-[11px] text-[#88a39b] mt-1">
                                        <span>0 (Deterministic)</span>
                                        <span>2 (Creative)</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[13px] font-bold text-[#15302f] mb-1.5 block">Max Tokens (optional)</label>
                                    <input 
                                        type="number" min="1" max="8000"
                                        value={maxTokens === null ? "" : maxTokens}
                                        onChange={e => setMaxTokens(e.target.value ? parseInt(e.target.value) : null)}
                                        disabled={!canEdit}
                                        placeholder="No limit"
                                        className="w-full border border-[#d9dedb] rounded-lg px-3 py-2 text-[13px] bg-[#fafbfa] focus:outline-none focus:border-[#4b8892] disabled:bg-[#f4f6f5] disabled:text-[#5f7173]"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Editor Footer / Save */}
                        {canEdit && (
                            <div className="px-6 py-4 border-t border-[#eef1ef] bg-[#fcfdfd] flex items-center justify-between">
                                <input 
                                    type="text"
                                    placeholder="Change note (required to save)..."
                                    value={changeNote}
                                    onChange={e => setChangeNote(e.target.value)}
                                    className="flex-1 max-w-md border border-[#d9dedb] rounded-lg px-3 py-2 text-[13px] bg-white focus:outline-none focus:border-[#4b8892] mr-4"
                                />
                                <button 
                                    onClick={handleSave}
                                    disabled={isSaving || missingPlaceholders.length > 0 || !changeNote.trim()}
                                    className="bg-[#004d57] text-white text-[13px] font-bold rounded-lg px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#003840] transition-colors"
                                >
                                    {isSaving ? "Saving..." : "Save Prompt"}
                                </button>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}
